/**
 * useSearchIndex Hook
 * 
 * Generic React hook for searching any Firestore collection via its search index.
 * Provides search functionality, rebuild capability, and statistics.
 * 
 * Architecture:
 * - Wraps SearchIndexRepository for React state management
 * - Provides loading states and error handling
 * - Debounced real-time search
 * - Manual rebuild trigger with progress tracking
 * 
 * Usage:
 * const { 
 *   search, 
 *   results, 
 *   loading, 
 *   rebuild, 
 *   stats,
 *   refreshStats 
 * } = useSearchIndex(PropertySearchIndex);
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { SearchIndexRepository } from '../models/SearchIndexRepository';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { useToast } from '../components/shared';

export const useSearchIndex = (searchIndexConfig, { loadStatsOnMount = false } = {}) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [error, setError] = useState(null);
  const toast = useToast();

  // Initialize repository (memoized: rebuilding it on every render churned the
  // search/healthCheck callbacks that depend on it).
  const repository = useMemo(
    () => new SearchIndexRepository(db, searchIndexConfig),
    [searchIndexConfig]
  );

  /**
   * Search the index
   * @param {string} searchTerm - Search query
   * @param {Object} filters - Optional filters
   * @param {number} maxResults - Max results to return
   */
  const search = useCallback(async (searchTerm, filters = {}, maxResults = 100) => {
    if (!searchTerm || !searchTerm.trim()) {
      setResults([]);
      return [];
    }

    setSearching(true);
    setError(null);

    try {
      const searchResults = await repository.search(searchTerm, filters, maxResults);
      setResults(searchResults);
      return searchResults;
    } catch (err) {
      console.error('[useSearchIndex] Search error:', err);
      setError(err.message);
      toast.error(`Search failed: ${err.message}`);
      return [];
    } finally {
      setSearching(false);
    }
  }, [repository, toast]);

  /**
   * Clear search results
   */
  const clearSearch = useCallback(() => {
    setResults([]);
    setError(null);
  }, []);

  /**
   * Rebuild entire index via Cloud Function
   * @param {Object} options - Rebuild options
   * @param {boolean} options.clearFirst - Clear existing index before rebuilding
   * @param {Function} progressCallback - Optional callback(processed, total)
   */
  const rebuild = useCallback(async (options = {}, progressCallback) => {
    setRebuilding(true);
    setError(null);

    try {
      
      // Use explicit function name from config or construct from collection
      const functionName = searchIndexConfig.functionNames?.rebuild || 
        `rebuild${searchIndexConfig.sourceCollection
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join('')}SearchIndex`;
      
      const rebuildFn = httpsCallable(functions, functionName);
      const result = await rebuildFn(options); // Pass options to Cloud Function

      if (result.data.success) {
        toast.success(`Index rebuilt: ${result.data.created} documents indexed in ${Math.round(result.data.durationMs / 1000)}s`);
        
        // Refresh stats after rebuild
        await refreshStats();
        
        return result.data;
      } else {
        throw new Error(result.data.error || 'Rebuild failed');
      }
    } catch (err) {
      console.error('[useSearchIndex] Rebuild error:', err);
      setError(err.message);
      toast.error(`Rebuild failed: ${err.message}`);
      throw err;
    } finally {
      setRebuilding(false);
    }
  }, [searchIndexConfig, functions, toast]);

  /**
   * Get index statistics via Cloud Function
   */
  const refreshStats = useCallback(async () => {
    setLoadingStats(true);
    setError(null);

    try {
      // Use explicit function name from config or construct from collection
      const functionName = searchIndexConfig.functionNames?.getStats || 
        `get${searchIndexConfig.sourceCollection
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join('')}SearchIndexStats`;
      
      const getStatsFn = httpsCallable(functions, functionName);
      const result = await getStatsFn();

      setStats(result.data);
      return result.data;
    } catch (err) {
      console.error('[useSearchIndex] Stats error:', err);
      setError(err.message);
      return null;
    } finally {
      setLoadingStats(false);
    }
  }, [searchIndexConfig, functions]);

  /**
   * Health check - verify index exists and is healthy
   */
  const healthCheck = useCallback(async () => {
    try {
      const health = await repository.healthCheck();
      return health;
    } catch (err) {
      console.error('[useSearchIndex] Health check error:', err);
      return {
        healthy: false,
        message: err.message,
        error: err
      };
    }
  }, [repository]);

  // Load stats on mount ONLY when asked (e.g. SearchIndexMonitor). refreshStats
  // calls the getStats Cloud Function, which scans the whole search index —
  // consumers that only need search() (e.g. usePropertiesData) must NOT trigger
  // it, or every Properties-tab open pays for a full-index scan.
  useEffect(() => {
    if (loadStatsOnMount) refreshStats();
  }, [loadStatsOnMount, refreshStats]);

  return {
    // Search
    search,
    clearSearch,
    results,
    searching,
    
    // Rebuild
    rebuild,
    rebuilding,
    
    // Stats
    stats,
    loadingStats,
    refreshStats,
    
    // Health
    healthCheck,
    
    // General
    loading: searching || rebuilding || loadingStats,
    error
  };
};

/**
 * Pre-configured hook for property search index
 */
export const usePropertySearchIndex = (options) => {
  const { PropertySearchIndex } = require('../models/SearchIndex');
  return useSearchIndex(PropertySearchIndex, options);
};

export default useSearchIndex;
